import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { Button, Card, Modal } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { NotificationContainer, NotificationManager } from "react-notifications";
import * as Yup from "yup";
import { Breadcrumb } from "@gull";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { caricaComuniTypeahed } from "app/views/tabellaPocr/Comuni/comuniService";
import jwtAuthService from "app/services/jwtAuthService";
import { authorRoleOptions, getQuery, getQueryPostDto } from "app/views/tabellaPocr/ServiceController";
import { handleTabSelect } from "app/utility/typeahead";
import EditorJ from "@gull/components/Jodit/EditorJ";
import { formattaDataOraHHmmss } from "app/utility/dataOra";
import LoadingSpinner from "@gull/components/Loading/LoadingSpinner";

const InfoAccount = () => {
    const location = useLocation();
    const hide = location.state?.hide;
    const edit = location.state?.edit ?? false;

    const data = jwtAuthService.getUser();
    const displayName = data?.user?.displayName;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const effectRan = useRef(false);

    const [comuniOptions, setComuniOptions] = useState([]);
    const [ambulatoriOptions, setAmbulatoriOptions] = useState([]);
    const [aslOptions, setAslOptions] = useState([]);
    const [esami, setEsami] = useState();
    const [numEsami, setNumEsami] = useState(0);
    const [isEsamiButtonDisabled, setEsamiButtonDisabled] = useState(true);
    const [isSaving, setIsSaving] = useState(false);


    // Stato per modali
    const [isRefertoModalOpen, setRefertoModalOpen] = useState(false);
    const [isFirmaModalOpen, setFirmaModalOpen] = useState(false);
    const [isEsamiModalOpen, setEsamiModalOpen] = useState(false);

    const routeSegments = hide
        ? [{ name: "Informazioni Account" }]
        : [
            { name: "Home", path: "/" },
            { name: "Informazioni Account" },
        ];

    const [contentReferto, setContentReferto] = useState('');
    const [contentFirma, setContentFirma] = useState('');
    const [initialValues, setInitialValues] = useState({
        // SEZIONE 1
        intestazione: displayName,
        idComune: 0,
        comuneAsString: "",
        cap: "",
        indirizzo: "",
        telefono: "",
        partitaIva: "",
        codiceFiscale: "",

        // SEZIONE 2
        codiceRegione: "",
        codiceAsl: "",
        codiceSts: "",
        pinSmartCard: "",
        authRoleFse: "",

        // SEZIONE 3
        idAmbulatorio: 0,
        ambulatorioAsString: "",

        // SEZIONE 4
        intestazioneReferto: "<p>Inserire intestazione referto...</p>",
        firmaMedico: "<p>Inserire firma medico...</p>",
        allineamentoFirma: "2",

        // UTILI PER MODIFICA
        idasl: 0,
        idstruttura: 0,
        idutente: "",
        idintestazione: 0,
        idsede: 0,
        idregistro: 0,
        isEdit: false,
        isLater: true,
    });

    useEffect(() => {
        // Previene la seconda chiamata in StrictMode
        if (effectRan.current) return;
        effectRan.current = true;

        // Carico gli ambulatori
        dispatch(getQuery("ProfiloFse/GetAmbulatori"))
            .then((data) => {
                setAmbulatoriOptions(data);
            })
            .catch((err) => console.log(err));

        // Carico le Asl
        dispatch(getQuery("Asl/ElencoAsl"))
            .then((data) => {
                data = data.sort((a, b) =>
                    a.regione.localeCompare(b.regione) || a.descrizione.localeCompare(b.descrizione)
                );
                setAslOptions(data);
            })
            .catch((err) => console.log(err));

        // Carico le informazioni se sono in edit
        if (edit) {
            dispatch(getQuery("ProfiloFse/GetProfilo"))
                .then((res) => {
                    if (res.isSuccess) {
                        // Destruttura "esami" dal resto delle proprietà
                        const { esami, ...otherValues } = res.data;
                        // Salvi i soli otherValues dentro initialValues
                        setInitialValues(otherValues);
                        // Intetestazione referto 
                        setContentReferto(otherValues.intestazioneReferto);
                        setContentFirma(otherValues.firmaMedico);
                        // E gestisci esami e relative variabili di stato
                        setEsami(esami);
                        setNumEsami(esami.length);
                        setEsamiButtonDisabled(esami.length === 0);
                    }
                    else {
                        NotificationManager.warning(res.message, "Attenzione",);
                    }
                })
                .catch((err) => console.log(err));
        }
    }, [dispatch, edit]);

    const handleComuneSearch = (query, setFieldValue) => {
        if (query.length >= 2) {
            dispatch(caricaComuniTypeahed(query))
                .then((data) => {
                    setComuniOptions(data);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const handleAmbulatorioChange = async (e, formikOnChange) => {
        formikOnChange(e);
        const idAmbulatorio = e.target.value;

        if (!idAmbulatorio) {
            setNumEsami(0);
            setEsami([]);
            setEsamiButtonDisabled(true);
            return;
        }

        try {
            const dataEsami = await dispatch(getQuery(`ProfiloFse/GetEsamiByAmbulatorio/${idAmbulatorio}`));
            setEsami(dataEsami);
            setNumEsami(dataEsami.length);
            setEsamiButtonDisabled(false);
        } catch (error) {
            console.log(error);
        }
    };

    const handleIntestazioneReferto = (e, formikOnBlur, values, setFieldValue) => {
        formikOnBlur(e);

        if (values.isLater || !edit) {
            const nuovaIntestazioneReferto = `
            <p><strong>${values.intestazione || ""}</strong>
            <br/>${values.indirizzo || ""} - ${values.comuneAsString.replace(/\s*\[.*?\]/g, "") || ""} - ${values.cap || ""}
            <br/>P.Iva: ${values.partitaIva || ""} | Tel: ${values.telefono || ""}
            </p>
            `;
            setFieldValue("intestazioneReferto", nuovaIntestazioneReferto, false);
            setContentReferto(nuovaIntestazioneReferto);
        }
    }

    const handleFirmaReferto = (e, formikOnBlur, values, setFieldValue) => {
        formikOnBlur(e);

        if (values.isLater || !edit) {
            const nuovaFirma = `
            <div><strong>${values.intestazione || ""}</strong></div>
            `;
            setFieldValue("firmaMedico", nuovaFirma, false);
            setContentFirma(nuovaFirma);
        }
    }

    const handleLater = () => {
        if (edit)
            navigate("/");
        else
            handleSubmit(initialValues, { setSubmitting: null });
    }

    const handleSubmit = (values, { setSubmitting }) => {
        setIsSaving(true);
        NotificationManager.listNotify.forEach(n => NotificationManager.remove({ id: n.id }));
        setSubmitting && setSubmitting(true);
        dispatch(getQueryPostDto("ProfiloFse/EditProfilo", values))
            .then((result) => {
                if (result.isSuccess) {
                    NotificationManager.success(result.message, "Salva");
                    setTimeout(() => {
                        navigate("/");
                    }, 1000);
                }
                else {
                    NotificationManager.error(result.message, "Errore");
                }
                setSubmitting && setSubmitting(false);
                setTimeout(() => {
                    setIsSaving(false);
                }, 800);
            })
            .catch((error) => {
                console.error(error);
                setSubmitting && setSubmitting(false);
                setIsSaving(false);
            });
    };

    const [consensi, setConsensi] = useState([]);
    const [isLoadingConsensi, setLoadingConsensi] = useState(true);

    useEffect(() => {
        loadConsensi();
    }, []);

    const loadConsensi = () => {
        dispatch(getQuery("RegistroTrattamenti/GetTrattamenti"))
            .then((res) => {
                setConsensi(res);
                setLoadingConsensi(false);
            })
            .catch((err) => {
                console.error("Errore nel caricamento consensi", err);
                setLoadingConsensi(false);
            });
    }

    const handleAggiornaConsensi = () => {
        const consensiModificabili = consensi.filter(c => c.tipologia !== 1);
        dispatch(getQueryPostDto("RegistroTrattamenti/SalvaMulti", consensiModificabili))
            .then((res) => {
                if (res.isSuccess) {
                    NotificationManager.success("Consensi aggiornati con successo", "OK");
                    loadConsensi();
                } else {
                    NotificationManager.error(res.message || "Errore nel salvataggio", "Errore");
                }
            })
            .catch((err) => {
                console.error("Errore durante il salvataggio dei consensi", err);
                NotificationManager.error("Errore imprevisto", "Errore");
            });
    };

    return (
        <div>
            <Breadcrumb routeSegments={routeSegments} />

            <section className="chekout-page">
                {initialValues.isLater &&
                    <div className="alert alert-success d-flex justify-content-between align-items-center">
                        <span className="flex-1">
                            👀 Vuoi guardarti un attimo intorno, poi penserai a registrarti?
                        </span>

                        <div className="flex gap-2">
                            <button
                                onClick={handleLater}
                                className="btn btn-primary"
                            >
                                Vai alla Dashboard
                            </button>
                        </div>
                    </div>
                }
                <div className="row">
                    <div className="col-3">
                        <div className="card">
                            <div className="card-body">
                                <div className="card-title">Perché devo inserire queste informazioni?</div>
                                <div className="ul-widget__body">
                                    <div className="ul-widget3">
                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">
                                                            Informazioni Studio
                                                        </span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione uno
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    1)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    I dati principali del tuo studio medico consentono l'inizializzazione corretta di tutte le procedure. Queste informazioni personalizzano il programma secondo le tue esigenze. Assicurati che i dati siano sempre aggiornati.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">Credenziali Fascicolo Sanitario Elettronico</span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione due
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    2)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    Inserisci le credenziali per accedere al Fascicolo Sanitario Elettronico in modo sicuro e conforme alle normative. Questo passaggio garantisce l’integrazione dei dati clinici e la condivisione in tempo reale. Proteggi sempre le credenziali come previsto dalle regole sulla privacy.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">Selezione Esami</span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione tre
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    3)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    Scegli l’ambulatorio appropriato in base alle esigenze cliniche del tuo studio. In questo modo, verranno associati automaticamente tutti gli esami pertinenti secondo il Nomenclatore Nazionale. Puoi verificare e confermare gli esami tramite l’apposito pulsante alla destra della scelta dell’ambulatorio.</p>
                                            </div>
                                        </div>
                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">Configurazione Referto</span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione quattro
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    4)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    Definisci le modalità di presentazione e stampa del referto per garantirne la massima chiarezza. Personalizza gli elementi informativi indispensabili come intestazione e firma. Così facendo, il referto rispetterà gli standard professionali del tuo studio.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card mt-3">
                            <div className="card-body">
                                <h6 className="card-title">Consensi al Trattamento</h6>
                                {isLoadingConsensi ? (
                                    <p>Caricamento consensi...</p>
                                ) : (
                                    <>
                                        {consensi.map((consenso) => (
                                            <div key={consenso.id} className="mb-4 border-bottom pb-3">
                                                <div className="mb-3">
                                                    <fieldset>
                                                        <h5 className="fw-bold">{consenso.tipologiaString}</h5>
                                                        <div className="mb-2">
                                                            <strong>Data consenso:</strong> {formattaDataOraHHmmss(consenso.dataConsenso)}
                                                        </div>
                                                        {consenso.tipologia === 1 ? (
                                                            <>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        disabled
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === true}
                                                                        readOnly
                                                                    /> Acconsento
                                                                </label>
                                                                <span className="mx-4"></span>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        disabled
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === false}
                                                                        readOnly
                                                                    /> Non acconsento
                                                                </label>
                                                                <div className="text-muted fst-italic">(Non modificabile)</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        id={`acconsento_${consenso.id}`}
                                                                        name={`consenso_${consenso.id}`}
                                                                        value="accept"
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === true}
                                                                        onChange={() => {
                                                                            setConsensi(prev =>
                                                                                prev.map(c => c.id === consenso.id ? { ...c, acconsento: true } : c)
                                                                            );
                                                                        }}
                                                                    /> Acconsento
                                                                </label>
                                                                <span className="mx-4"></span>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        id={`nonacconsento_${consenso.id}`}
                                                                        name={`consenso_${consenso.id}`}
                                                                        value="decline"
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === false}
                                                                        onChange={() => {
                                                                            setConsensi(prev =>
                                                                                prev.map(c => c.id === consenso.id ? { ...c, acconsento: false } : c)
                                                                            );
                                                                        }}
                                                                    /> Non acconsento
                                                                </label>
                                                            </>
                                                        )}
                                                    </fieldset>
                                                </div>
                                            </div>
                                        ))}

                                        <Button className="w-100 mt-2" variant="primary" onClick={handleAggiornaConsensi}>
                                            Aggiorna Consensi
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-9">
                        <div className="container">
                            <Card className="mb-3">
                                <Card.Body>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={handleSubmit}
                                        enableReinitialize={true}
                                        validateOnBlur={true}
                                        validateOnChange={false}
                                    >
                                        {({
                                            values,
                                            handleChange,
                                            handleBlur,
                                            setFieldValue,
                                            errors,
                                            touched,
                                            isSubmitting
                                        }) => {
                                            return (
                                                <>
                                                    {
                                                        isSaving && (
                                                            <div className="d-flex justify-content-center my-3">
                                                                <LoadingSpinner message="Salvataggio in corso..." />
                                                            </div>
                                                        )
                                                    }
                                                    {
                                                        !isSaving &&
                                                        <Form autoComplete="off">
                                                            <h5>1) Informazioni Studio</h5>
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Intestazione</label>
                                                                    <input
                                                                        type="text"
                                                                        name="intestazione"
                                                                        id="intestazione"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                            handleFirmaReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.intestazione}
                                                                    />
                                                                    {errors.intestazione && touched.intestazione && (
                                                                        <div className="text-danger">{errors.intestazione}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label">Comune</label>
                                                                    <AsyncTypeahead
                                                                        id="idComune"
                                                                        name="idComune"
                                                                        labelKey="descrizione"
                                                                        selectHintOnEnter={true}
                                                                        onSearch={(query) => handleComuneSearch(query, setFieldValue)}
                                                                        options={comuniOptions}
                                                                        onKeyDown={(e) => handleTabSelect(e, comuniOptions, setFieldValue, "idComune", "comuneAsString", null, "cap")}
                                                                        placeholder="Ricerca comune..."
                                                                        onBlur={(e) => handleIntestazioneReferto(e, handleBlur, values, setFieldValue)}
                                                                        onChange={(selected) => {
                                                                            if (selected && selected.length > 0) {
                                                                                setFieldValue("idComune", String(selected[0].id));
                                                                                setFieldValue("comuneAsString", selected[0].descrizione);
                                                                                setFieldValue("cap", selected[0].extra);
                                                                            } else {
                                                                                setFieldValue("idComune", "");
                                                                                setFieldValue("comuneAsString", "");
                                                                                setFieldValue("cap", "");
                                                                            }
                                                                        }}
                                                                        selected={
                                                                            values.idComune
                                                                                ? [{ id: parseInt(values.idComune, 10), descrizione: values.comuneAsString }]
                                                                                : []
                                                                        }
                                                                    />
                                                                    {errors.idComune && touched.idComune && (
                                                                        <div className="text-danger">{errors.idComune}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-2 mb-3">
                                                                    <label className="form-label">CAP</label>
                                                                    <input
                                                                        type="text"
                                                                        name="cap"
                                                                        id="cap"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.cap}
                                                                    />
                                                                    {errors.cap && touched.cap && (
                                                                        <div className="text-danger">{errors.cap}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="row">
                                                                <div className="col-md-8 mb-3">
                                                                    <label className="form-label">Indirizzo</label>
                                                                    <input
                                                                        type="text"
                                                                        name="indirizzo"
                                                                        id="indirizzo"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.indirizzo}
                                                                    />
                                                                    {errors.indirizzo && touched.indirizzo && (
                                                                        <div className="text-danger">{errors.indirizzo}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label">Telefono</label>
                                                                    <input
                                                                        type="text"
                                                                        name="telefono"
                                                                        id="telefono"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.telefono}
                                                                    />
                                                                    {errors.telefono && touched.telefono && (
                                                                        <div className="text-danger">{errors.telefono}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Partita Iva</label>
                                                                    <input
                                                                        type="text"
                                                                        name="partitaIva"
                                                                        id="partitaIva"
                                                                        maxLength={11}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue)
                                                                        }}
                                                                        value={values.partitaIva}
                                                                    />
                                                                    {errors.partitaIva && touched.partitaIva && (
                                                                        <div className="text-danger">{errors.partitaIva}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Codice Fiscale</label>
                                                                    <input
                                                                        type="text"
                                                                        name="codiceFiscale"
                                                                        id="codiceFiscale"
                                                                        maxLength={16}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={(e) => {
                                                                            e.target.value = e.target.value.toUpperCase();
                                                                            handleChange(e);
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        value={values.codiceFiscale}
                                                                    />
                                                                    {errors.codiceFiscale && touched.codiceFiscale && (
                                                                        <div className="text-danger">{errors.codiceFiscale}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <hr className="bg-primary" />
                                                            <h5>2) Credenziali Fascicolo Sanitario Elettronico</h5>
                                                            <div className="row">
                                                                <div className="col-9 mb-3">
                                                                    <label className="form-label">[Codice regione] - Asl di appartenenza</label>
                                                                    <div className="d-flex align-items-center">
                                                                        <select
                                                                            name="idasl"
                                                                            id="idasl"
                                                                            className="form-control"
                                                                            onChange={(e) => {
                                                                                handleChange(e);
                                                                                const selectedIndex = e.target.selectedIndex;
                                                                                const optionElement = e.target.options[selectedIndex];
                                                                                setFieldValue("codiceRegione", optionElement.dataset.codice);
                                                                                setFieldValue("codiceAsl", optionElement.dataset.asl);
                                                                            }}
                                                                            onBlur={handleBlur}
                                                                            value={values.idasl}
                                                                        >
                                                                            <option value="">Seleziona Asl...</option>
                                                                            {aslOptions.map((asl) => (
                                                                                <option key={asl.id} value={asl.id} data-codice={asl.codRegione} data-asl={asl.codAzienda}>
                                                                                    {`[${asl.codRegione}] ${asl.regione.toUpperCase()}  -  ${asl.descrizione.toUpperCase()}`}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    {errors.idasl && touched.idasl && (
                                                                        <div className="text-danger">{errors.idasl}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-3 mb-3">
                                                                    <label className="form-label">Codice Ufficio</label>
                                                                    <input
                                                                        type="text"
                                                                        name="codiceSts"
                                                                        id="codiceSts"
                                                                        maxLength={8}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        value={values.codiceSts}
                                                                    />
                                                                    {errors.codiceSts && touched.codiceSts && (
                                                                        <div className="text-danger">{errors.codiceSts}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label" htmlFor="authRoleFse">
                                                                        Ruolo dell'autore
                                                                    </label>
                                                                    <select
                                                                        id="authRoleFse"
                                                                        name="authRoleFse"
                                                                        className="form-control"
                                                                        value={values.authRoleFse}
                                                                        onChange={handleChange}
                                                                    >
                                                                        <option value="">-- Scegli un ruolo --</option>
                                                                        {authorRoleOptions
                                                                            .slice()
                                                                            .sort((a, b) => a.value.localeCompare(b.value))
                                                                            .map(opt => (
                                                                                <option key={opt.value} value={opt.value}>
                                                                                    {opt.label}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                    {errors.authRoleFse && touched.authRoleFse && (
                                                                        <div className="text-danger mt-1 ml-2">
                                                                            {errors.authRoleFse}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <hr className="bg-primary" />
                                                            <h5>3) Selezione Esami</h5>
                                                            <div className="row">
                                                                <div className="col-9 mb-3">
                                                                    <label className="form-label">Ambulatorio</label>
                                                                    <div className="d-flex align-items-center">
                                                                        {(initialValues.idAmbulatorio == 0 || !edit) &&
                                                                            <select
                                                                                name="idAmbulatorio"
                                                                                id="idAmbulatorio"
                                                                                className="form-control"
                                                                                onChange={(e) => handleAmbulatorioChange(e, handleChange)}
                                                                                onBlur={handleBlur}
                                                                                value={values.idAmbulatorio}
                                                                            >
                                                                                <option value="">Seleziona Ambulatorio...</option>
                                                                                {ambulatoriOptions.map((amb) => (
                                                                                    <option key={amb.id} value={amb.id}>
                                                                                        {amb.descrizione.toUpperCase()}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        }
                                                                        {(initialValues.idAmbulatorio > 0 && edit) &&
                                                                            <>
                                                                                <input
                                                                                    type="hidden"
                                                                                    name="idAmbulatorio"
                                                                                    id="idAmbulatorio"
                                                                                    value={values.idAmbulatorio}
                                                                                />
                                                                                <input
                                                                                    type="text"
                                                                                    name="ambulatorioText"
                                                                                    className="form-control"
                                                                                    disabled={true}
                                                                                    value={values.ambulatorioAsString}
                                                                                />
                                                                            </>
                                                                        }
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-primary mx-4"
                                                                            disabled={isEsamiButtonDisabled}
                                                                            onClick={() => setEsamiModalOpen(true)}
                                                                        >
                                                                            {numEsami}
                                                                        </button>
                                                                    </div>
                                                                    {errors.idAmbulatorio && touched.idAmbulatorio && (
                                                                        <div className="text-danger">{errors.idAmbulatorio}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-12 ul-widget3-body">
                                                                    <p className="ul-widget3-text text-mute">
                                                                        Quando scegli l'ambulatorio, il sistema assegna in automatico tutti gli esami del NOMECLATORE nazionale legati a quell’ambulatorio.
                                                                        <br />
                                                                        Per vedere quali esami verrranno inseriti, basta <b>cliccare sul pulsante</b> accanto al menu di selezione.
                                                                        <br />
                                                                        <i><b>Attenzione: una volta effettuato il salvataggio, questa sezione non sarà più modificabile.</b></i>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <hr className="bg-primary" />
                                                            <h5>4) Configurazione Referto</h5>
                                                            <div className="row mb-3">
                                                                <div className="col-8">
                                                                    <label className="form-label">Intestazione referto</label>
                                                                    <div
                                                                        className="border p-2"
                                                                        name="intestazioneReferto"
                                                                        id="intestazioneReferto"
                                                                        style={{ minHeight: "100px" }}
                                                                        dangerouslySetInnerHTML={{ __html: values.intestazioneReferto }}
                                                                    />
                                                                    <Button
                                                                        variant="info"
                                                                        className="mt-2"
                                                                        onClick={() => setRefertoModalOpen(true)}
                                                                    >
                                                                        Modifica Intestazione Referto
                                                                    </Button>
                                                                </div>
                                                                <div className="col-4">
                                                                    <label className="form-label">Firma Medico</label>
                                                                    <div
                                                                        className="border p-2"
                                                                        name="firmaMedico"
                                                                        id="firmaMedico"
                                                                        style={{ minHeight: "100px" }}
                                                                        dangerouslySetInnerHTML={{ __html: values.firmaMedico }}
                                                                    />
                                                                    <Button
                                                                        variant="info"
                                                                        className="mt-2"
                                                                        onClick={() => setFirmaModalOpen(true)}
                                                                    >
                                                                        Modifica Firma Medico
                                                                    </Button>
                                                                </div>
                                                            </div>

                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <label className="form-label">Allineamento Firma</label>
                                                                    <select
                                                                        name="allineamentoFirma"
                                                                        id="allineamentoFirma"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        value={values.allineamentoFirma}
                                                                    >
                                                                        <option value="0">Sinistra</option>
                                                                        <option value="1">Centro</option>
                                                                        <option value="2">Destra</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <hr />
                                                            <div className="d-flex justify-content-end">
                                                                <Button
                                                                    className="w-25"
                                                                    variant="primary"
                                                                    type="submit"
                                                                    disabled={isSubmitting}
                                                                >
                                                                    {isSubmitting ? "Salvataggio..." : "Salva"}
                                                                </Button>
                                                            </div>

                                                            {/* MODALE ESAMI */}
                                                            <Modal
                                                                id="modaleEsami"
                                                                show={isEsamiModalOpen}
                                                                size="lg"
                                                                centered
                                                                onHide={() => setEsamiModalOpen(false)}
                                                                scrollable
                                                                backdrop="static"
                                                            >
                                                                <Modal.Header className="bg-primary text-white d-flex flex-wrap justify-content-between align-items-center">
                                                                    <h5 className="text-white">Lista Esami</h5>
                                                                    <i
                                                                        className="i-Close-Window cursor-pointer text-18"
                                                                        onClick={() => setEsamiModalOpen(false)}
                                                                    ></i>
                                                                </Modal.Header>
                                                                <Modal.Body>
                                                                    <div>
                                                                        <ol>
                                                                            {esami && esami.map((esame, index) => (
                                                                                <li className="fw-bold" key={index}>
                                                                                    <span className="fw-light">{esame.descrizione?.toUpperCase() ?? esame.esameAsString.toUpperCase()}</span>
                                                                                </li>
                                                                            ))}
                                                                        </ol>
                                                                    </div>
                                                                </Modal.Body>
                                                                <Modal.Footer>
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        type="button"
                                                                        onClick={() => setEsamiModalOpen(false)}
                                                                    >
                                                                        Chiudi
                                                                    </button>
                                                                </Modal.Footer>
                                                            </Modal>

                                                            {/* MODALE REFERTO */}
                                                            <Modal
                                                                id="modaleRerferto"
                                                                show={isRefertoModalOpen}
                                                                size="xl"
                                                                centered
                                                                onHide={() => setRefertoModalOpen(false)}
                                                                backdrop="static"
                                                            >
                                                                <Modal.Header className="bg-primary text-white d-flex flex-wrap justify-content-between align-items-center">
                                                                    <h5 className="text-white">Modifica Intestazione Referto</h5>
                                                                    <i
                                                                        className="i-Close-Window cursor-pointer text-18"
                                                                        onClick={() => setRefertoModalOpen(false)}
                                                                    ></i>
                                                                </Modal.Header>
                                                                <Modal.Body>
                                                                    <EditorJ
                                                                        //placeholder="Crea un preimpostato..."
                                                                        onChange={(content) => {
                                                                            setFieldValue("intestazioneReferto", content);
                                                                            console.log("Intestazione", values.intestazioneReferto);
                                                                        }}
                                                                        content={contentReferto}
                                                                        adattaStyle={false}
                                                                        nascondiSalva={true}
                                                                    />
                                                                </Modal.Body>
                                                                <Modal.Footer>
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setRefertoModalOpen(false);
                                                                            setContentReferto(values.intestazioneReferto);
                                                                        }}
                                                                    >
                                                                        Chiudi
                                                                    </button>
                                                                </Modal.Footer>
                                                            </Modal>

                                                            {/* MODALE FIRMA */}
                                                            <Modal
                                                                id="modaleFirma"
                                                                show={isFirmaModalOpen}
                                                                size="xl"
                                                                centered
                                                                onHide={() => setFirmaModalOpen(false)}
                                                                backdrop="static"
                                                            >
                                                                <Modal.Header className="bg-primary text-white d-flex flex-wrap justify-content-between align-items-center">
                                                                    <h5 className="text-white">Modifica Firma Medico</h5>
                                                                    <i
                                                                        className="i-Close-Window cursor-pointer text-18"
                                                                        onClick={() => setFirmaModalOpen(false)}
                                                                    ></i>
                                                                </Modal.Header>
                                                                <Modal.Body>
                                                                    <EditorJ
                                                                        onChange={(content) => {
                                                                            setFieldValue("firmaMedico", content);
                                                                        }}
                                                                        content={contentFirma}
                                                                        adattaStyle={false}
                                                                        nascondiSalva={true}
                                                                    />
                                                                </Modal.Body>
                                                                <Modal.Footer>
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setFirmaModalOpen(false);
                                                                            setContentFirma(values.firmaMedico);
                                                                        }}
                                                                    >
                                                                        Chiudi
                                                                    </button>
                                                                </Modal.Footer>
                                                            </Modal>
                                                        </Form>
                                                    }
                                                </>
                                            );
                                        }}
                                    </Formik>
                                </Card.Body>
                            </Card>
                            <NotificationContainer />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

const validationSchema = Yup.object().shape({
    // Tutti i campi obbligatori:
    intestazione: Yup.string().required("Campo obbligatorio"),
    idComune: Yup.string().required("Campo obbligatorio"),
    cap: Yup.string().required("Campo obbligatorio"),
    indirizzo: Yup.string().required("Campo obbligatorio"),
    telefono: Yup.string().required("Campo obbligatorio"),
    partitaIva: Yup.string().max(11, "Max 11 caratteri"),
    codiceFiscale: Yup.string().required("Campo obbligatorio").max(16, "Max 16 caratteri"),
    idAmbulatorio: Yup.string().required("Campo obbligatorio"),
});

export default InfoAccount;
